
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { Reservation, Table, CategoryDefinition, RestaurantConfig } from '../types';
import { ChevronLeft, ChevronRight, Clock, Users, Calendar as CalendarIcon, Target, Trophy, Activity, Zap, Dumbbell, Volleyball, Pencil, Minus, Plus, GripHorizontal } from 'lucide-react';
import { ReservationModal } from './ReservationModal';

interface AdminCalendarViewProps {
  reservations: Reservation[];
  tables: Table[];
  categories: CategoryDefinition[];
  onEditReservation: (res: Reservation) => void;
  onMakeReservation: (res: Reservation) => void;
  config: RestaurantConfig;
}

type ViewType = 'DAY' | 'WEEK' | 'MONTH';

interface DragState {
  type: 'MOVE' | 'RESIZE';
  resId: string;
  originalTime: string;
  originalTableId: string;
  originalDate: string;
  originalDuration: number;
  startY: number;
  startX: number;
  initialTop: number;
  currentTop: number;
  currentLeft: number;
  currentDuration: number;
  targetTableId: string;
  targetDate: string;
  hasMoved: boolean;
}

const ROW_HEIGHT = 80; 
const COL_WIDTH_DAY = 180;
const COL_WIDTH_WEEK = 150;
const COLLAPSED_DAY_WIDTH = 60;
const TIME_COL_WIDTH = 64; 

export const AdminCalendarView: React.FC<AdminCalendarViewProps> = ({
  reservations,
  tables,
  categories,
  onEditReservation,
  onMakeReservation,
  config
}) => {
  const [viewType, setViewType] = useState<ViewType>('DAY');
  const [currentDate, setCurrentDate] = useState(new Date());
  const [collapsedDays, setCollapsedDays] = useState<Set<string>>(new Set());
  
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingReservation, setEditingReservation] = useState<Reservation | null>(null);
  const [modalInitialData, setModalInitialData] = useState<Partial<Reservation>>({});

  const [dragging, setDragging] = useState<DragState | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const hours = useMemo(() => {
    const h = [];
    for (let i = 8; i <= 23; i++) h.push(i);
    return h;
  }, []);

  const formatDate = (date: Date) => date.toISOString().split('T')[0];

  const sortedTables = useMemo(() => 
    [...tables].sort((a, b) => a.name.localeCompare(b.name, undefined, { numeric: true })), 
    [tables]
  );

  const navigateDate = (amount: number) => {
    const newDate = new Date(currentDate);
    if (viewType === 'DAY') newDate.setDate(currentDate.getDate() + amount);
    else if (viewType === 'WEEK') newDate.setDate(currentDate.getDate() + amount * 7);
    else if (viewType === 'MONTH') newDate.setMonth(currentDate.getMonth() + amount);
    setCurrentDate(newDate);
  };

  const toggleDayCollapse = (dateStr: string) => {
    setCollapsedDays(prev => {
      const next = new Set(prev);
      if (next.has(dateStr)) next.delete(dateStr);
      else next.add(dateStr);
      return next;
    });
  };

  const handleSlotClick = (tableId: string, hourVal: number, dateStr: string) => {
    const h = Math.floor(hourVal);
    const m = (hourVal % 1) === 0.5 ? '30' : '00';
    
    setEditingReservation(null);
    setModalInitialData({
      tableId,
      time: `${h.toString().padStart(2, '0')}:${m}`,
      date: dateStr,
      durationMinutes: 60,
      partySize: 2
    });
    setIsModalOpen(true);
  };

  const getWeekDays = (date: Date) => {
    const start = new Date(date);
    const day = start.getDay();
    const diff = start.getDate() - (day === 0 ? 6 : day - 1);
    start.setDate(diff);
    return Array.from({ length: 7 }, (_, i) => {
      const d = new Date(start); d.setDate(start.getDate() + i);
      return d;
    });
  };

  const getMonthDays = (date: Date) => {
    const start = new Date(date.getFullYear(), date.getMonth(), 1);
    const end = new Date(date.getFullYear(), date.getMonth() + 1, 0);
    const days = [];
    const startPadding = start.getDay() === 0 ? 6 : start.getDay() - 1;
    for (let i = startPadding; i > 0; i--) {
      const d = new Date(start); d.setDate(start.getDate() - i);
      days.push({ date: d, isPadding: true });
    }
    for (let i = 1; i <= end.getDate(); i++) {
      days.push({ date: new Date(date.getFullYear(), date.getMonth(), i), isPadding: false });
    }
    return days;
  };

  const getTopByTime = (time: string) => {
    const [h, m] = time.split(':').map(Number);
    return (h - 8) * ROW_HEIGHT + (m / 60) * ROW_HEIGHT;
  };

  const getTimeByTop = (top: number) => {
    const totalMinutes = (top / ROW_HEIGHT) * 60;
    const h = Math.floor(totalMinutes / 60) + 8;
    const m = Math.round((totalMinutes % 60) / 15) * 15;
    const finalH = m === 60 ? h + 1 : h;
    const finalM = m === 60 ? 0 : m;
    return `${finalH.toString().padStart(2, '0')}:${finalM.toString().padStart(2, '0')}`;
  };

  const getEndTime = (time: string, duration: number) => {
    try {
      const [h, m] = time.split(':').map(Number);
      const date = new Date();
      date.setHours(h);
      date.setMinutes(m + duration);
      return date.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit' });
    } catch (e) {
      return "--:--";
    }
  };

  const isSlotClosed = (date: Date, hour: number) => {
    const dayNames = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];
    const dayName = dayNames[date.getDay()];
    const daySched = config.schedule[dayName];
    if (!daySched || daySched.isClosed) return true;
    const openHour = parseInt(daySched.open.split(':')[0]);
    const closeHour = parseInt(daySched.close.split(':')[0]);
    return hour < openHour || hour >= closeHour;
  };

  const isDarkMode = config.theme?.darkMode;

  const closedPatternStyle: React.CSSProperties = {
    backgroundColor: isDarkMode ? '#1e293b' : '#fcfdfe',
    backgroundImage: isDarkMode 
      ? 'repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(255, 255, 255, 0.05) 8px, rgba(255, 255, 255, 0.05) 9px)'
      : 'repeating-linear-gradient(45deg, transparent, transparent 8px, rgba(203, 213, 225, 0.15) 8px, rgba(203, 213, 225, 0.15) 9px)',
    cursor: 'not-allowed',
    pointerEvents: 'none',
    borderRight: isDarkMode ? '1px solid #334155' : '1px solid #e2e8f0'
  };

  const handleMouseDown = (e: React.MouseEvent, res: Reservation) => {
    e.stopPropagation();
    setDragging({
      type: 'MOVE',
      resId: res.id,
      originalTime: res.time,
      originalTableId: res.tableId,
      originalDate: res.date,
      originalDuration: res.durationMinutes,
      startY: e.clientY,
      startX: e.clientX,
      initialTop: getTopByTime(res.time),
      currentTop: getTopByTime(res.time),
      currentLeft: 0,
      currentDuration: res.durationMinutes,
      targetTableId: res.tableId,
      targetDate: res.date,
      hasMoved: false
    });
  };

  const handleResizeMouseDown = (e: React.MouseEvent, res: Reservation) => {
    e.stopPropagation();
    setDragging({
      type: 'RESIZE',
      resId: res.id,
      originalTime: res.time,
      originalTableId: res.tableId,
      originalDate: res.date,
      originalDuration: res.durationMinutes,
      startY: e.clientY,
      startX: e.clientX,
      initialTop: getTopByTime(res.time),
      currentTop: getTopByTime(res.time),
      currentLeft: 0,
      currentDuration: res.durationMinutes,
      targetTableId: res.tableId,
      targetDate: res.date,
      hasMoved: false
    });
  };

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      if (!dragging) return;
      const container = containerRef.current;
      if (!container) return;
      const deltaY = e.clientY - dragging.startY;
      const deltaX = e.clientX - dragging.startX;
      const hisSignificantMovement = Math.abs(deltaX) > 3 || Math.abs(deltaY) > 3;

      if (dragging.type === 'MOVE') {
        let newTableId = dragging.originalTableId;
        let newDate = dragging.originalDate;
        const firstCol = container.querySelector('.calendar-col');
        const actualColWidth = firstCol ? firstCol.clientWidth : (viewType === 'DAY' ? COL_WIDTH_DAY : COL_WIDTH_WEEK);

        if (viewType === 'DAY') {
          const colIndex = sortedTables.findIndex(t => t.id === dragging.originalTableId);
          const colShift = Math.round(deltaX / actualColWidth);
          const newColIndex = Math.max(0, Math.min(sortedTables.length - 1, colIndex + colShift));
          newTableId = sortedTables[newColIndex].id;
        } else if (viewType === 'WEEK') {
          const weekDays = getWeekDays(currentDate);
          const containerRect = container.getBoundingClientRect();
          const scrollX = container.scrollLeft;
          const relativeX = e.clientX - containerRect.left + scrollX - TIME_COL_WIDTH;
          let accumulatedWidth = 0;
          let foundDay = weekDays[0];
          let foundTableId = sortedTables[0].id;
          for (const day of weekDays) {
            const dStr = formatDate(day);
            const isCollapsed = collapsedDays.has(dStr);
            const dayWidth = isCollapsed ? COLLAPSED_DAY_WIDTH : (sortedTables.length * actualColWidth);
            if (relativeX < accumulatedWidth + dayWidth) {
              foundDay = day;
              if (!isCollapsed) {
                const innerX = relativeX - accumulatedWidth;
                const tableIdx = Math.max(0, Math.min(sortedTables.length - 1, Math.floor(innerX / actualColWidth)));
                foundTableId = sortedTables[tableIdx].id;
              } else {
                foundTableId = dragging.originalTableId;
              }
              break;
            }
            accumulatedWidth += dayWidth;
          }
          newDate = formatDate(foundDay);
          newTableId = foundTableId;
        }

        setDragging(prev => prev ? ({
          ...prev,
          currentTop: prev.initialTop + deltaY,
          currentLeft: deltaX,
          targetTableId: newTableId,
          targetDate: newDate,
          hasMoved: hisSignificantMovement
        }) : null);
      } else if (dragging.type === 'RESIZE') {
        const additionalMinutes = (deltaY / ROW_HEIGHT) * 60;
        const snappedMinutes = Math.round(additionalMinutes / 15) * 15;
        const newDuration = Math.max(15, dragging.originalDuration + snappedMinutes);
        setDragging(prev => prev ? ({ ...prev, currentDuration: newDuration, hasMoved: hisSignificantMovement }) : null);
      }
    };

    const handleMouseUp = (e: MouseEvent) => {
      if (!dragging) return;
      const res = reservations.find(r => r.id === dragging.resId);
      if (!res) { setDragging(null); return; }
      if (!dragging.hasMoved) {
        setEditingReservation(res);
        setIsModalOpen(true);
        setDragging(null);
        return;
      }
      if (dragging.type === 'MOVE') {
        const snappedTop = Math.max(0, dragging.currentTop);
        const newTime = getTimeByTop(snappedTop);
        if (newTime !== res.time || dragging.targetTableId !== res.tableId || dragging.targetDate !== res.date) {
          onEditReservation({ ...res, time: newTime, tableId: dragging.targetTableId, date: dragging.targetDate });
        }
      } else if (dragging.type === 'RESIZE') {
        if (dragging.currentDuration !== res.durationMinutes) {
          onEditReservation({ ...res, durationMinutes: dragging.currentDuration });
        }
      }
      setDragging(null);
    };

    if (dragging) {
      window.addEventListener('mousemove', handleMouseMove);
      window.addEventListener('mouseup', handleMouseUp);
    }
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [dragging, reservations, viewType, sortedTables, currentDate, collapsedDays, onEditReservation]);

  const getReservationBlockStyle = (res: Reservation, allInColumn: Reservation[]) => {
    const isDraggingThis = dragging?.resId === res.id;
    const isResizingThis = isDraggingThis && dragging?.type === 'RESIZE';
    const height = ( (isResizingThis ? dragging.currentDuration : res.durationMinutes) / 60) * ROW_HEIGHT;
    const top = isDraggingThis && dragging.type === 'MOVE' ? dragging.currentTop : getTopByTime(res.time);

    const overlapping = allInColumn.filter(other => {
      if (other.id === res.id) return false;
      const s1 = getTopByTime(res.time);
      const e1 = s1 + (res.durationMinutes / 60) * ROW_HEIGHT;
      const s2 = getTopByTime(other.time);
      const e2 = s2 + (other.durationMinutes / 60) * ROW_HEIGHT;
      return s1 < e2 && e1 > s2;
    });

    let left = '4px';
    let width = 'calc(100% - 8px)';

    if (overlapping.length > 0 && !isDraggingThis) {
      const sorted = [res, ...overlapping].sort((a, b) => {
        if (a.time !== b.time) return a.time.localeCompare(b.time);
        return a.id.localeCompare(b.id);
      });
      const index = sorted.findIndex(s => s.id === res.id);
      const colWidthPercent = 100 / sorted.length;
      left = `${index * colWidthPercent}%`;
      width = `${colWidthPercent}%`;
    }

    const confirmedColor = config.theme?.calendarConfirmedColor || 'var(--color-secondary)';
    const pendingColor = config.theme?.calendarPendingColor || '#f97316';
    const bgColor = res.status === 'PENDING' ? pendingColor : confirmedColor;

    if (isDraggingThis) {
      const container = containerRef.current;
      let blockWidth = (viewType === 'DAY' ? COL_WIDTH_DAY : COL_WIDTH_WEEK) - 8;
      if (container) {
          const firstCol = container.querySelector('.calendar-col');
          if (firstCol) blockWidth = firstCol.clientWidth - 8;
      }
      return { 
        top: `${top}px`, height: `${height}px`, width: `${blockWidth}px`,
        left: dragging.type === 'MOVE' ? `${dragging.currentLeft + 4}px` : left, 
        zIndex: 200, opacity: 0.9, pointerEvents: 'none' as const,
        boxShadow: '0 20px 25px -5px rgb(0 0 0 / 0.1)', backgroundColor: bgColor
      };
    }

    return { top: `${top}px`, height: `${height}px`, left, width, zIndex: 10, backgroundColor: bgColor };
  };

  const getCategoryIcon = (categoryId: string) => {
    const cat = categories.find(c => c.id === categoryId);
    const iconType = cat?.icon || '📍';
    if (iconType.length <= 2 || !/^[a-zA-Z]+$/.test(iconType)) return <span className="text-xs">{iconType}</span>;
    switch(iconType) {
        case 'trophy': return <Trophy size={12} />;
        case 'target': return <Target size={12} />;
        case 'users': return <Users size={12} />;
        case 'activity': return <Activity size={12} />;
        case 'zap': return <Zap size={12} />;
        case 'dumbbell': return <Dumbbell size={12} />;
        case 'volleyball': return <Volleyball size={12} />;
        default: return <Trophy size={12} />;
    }
  };

  const renderDayView = () => {
    const dateStr = formatDate(currentDate);
    const headerHeight = 'h-12';
    return (
      <div className="flex-1 flex flex-col overflow-hidden bg-white rounded-xl border border-slate-200 shadow-sm w-full">
        <div className="overflow-auto relative flex-1" ref={containerRef}>
          <div className="sticky top-0 z-[50] flex w-full min-w-max">
            <div className={`${headerHeight} bg-slate-100 border-r border-b border-slate-200 shrink-0 sticky left-0 z-[60]`} style={{ width: `${TIME_COL_WIDTH}px` }}></div>
            <div className={`flex flex-1 ${headerHeight} bg-white border-b border-slate-200 shadow-sm items-center`}>
              {sortedTables.map(t => (
                <div key={t.id} style={{ flex: `1 0 ${COL_WIDTH_DAY}px` }} className="px-4 border-r border-slate-100 flex items-center justify-center gap-1.5 h-full">
                  <span className="text-slate-400">{getCategoryIcon(t.category)}</span>
                  <span className={`text-[10px] font-black uppercase tracking-widest truncate block ${isDarkMode ? 'theme-text-primary' : 'text-slate-800'}`}>{t.name}</span>
                </div>
              ))}
            </div>
          </div>
          <div className="relative flex w-full min-w-max">
            <div className="sticky left-0 z-[40] bg-slate-50 border-r border-slate-200 flex-shrink-0" style={{ width: `${TIME_COL_WIDTH}px` }}>
              {hours.map(h => (
                <div key={h} style={{ height: `${ROW_HEIGHT}px` }} className="border-b border-slate-200 px-2 pt-1 text-[10px] font-black text-slate-400 text-right box-border bg-slate-50 relative">
                  {h}:00
                </div>
              ))}
            </div>
            <div className="flex flex-1">
              {sortedTables.map(t => {
                // Filter out CANCELLED_BY_GUEST so slots are visually free
                const columnReservations = reservations.filter(r => r.tableId === t.id && r.date === dateStr && r.status !== 'CANCELLED_BY_GUEST');
                return (
                  <div key={t.id} style={{ flex: `1 0 ${COL_WIDTH_DAY}px` }} className="calendar-col border-r border-slate-100 relative group transition-colors select-none box-border">
                    {hours.map(h => {
                      const isClosed = isSlotClosed(currentDate, h);
                      return (
                        <div key={h} style={{ height: `${ROW_HEIGHT}px`, ...(isClosed ? closedPatternStyle : {}) }} className="border-b border-slate-200 box-border relative">
                          {!isClosed && (
                            <>
                              <div onClick={() => handleSlotClick(t.id, h, dateStr)} className="absolute top-0 left-0 right-0 h-1/2 hover:bg-emerald-50/40 cursor-cell transition-colors"></div>
                              <div onClick={() => handleSlotClick(t.id, h + 0.5, dateStr)} className="absolute bottom-0 left-0 right-0 h-1/2 hover:bg-emerald-50/40 cursor-cell transition-colors border-t border-slate-100/30"></div>
                            </>
                          )}
                        </div>
                      );
                    })}
                    {columnReservations.map(res => (
                      <div key={res.id} style={getReservationBlockStyle(res, columnReservations)} onMouseDown={(e) => handleMouseDown(e, res)} className="absolute border border-white/20 rounded-lg p-3 text-white shadow-md hover:brightness-110 cursor-grab active:cursor-grabbing overflow-hidden group/block">
                        <div className="text-sm font-medium truncate uppercase tracking-normal leading-tight">{res.customerName}</div>
                        <div className="flex items-center gap-1 text-[10px] font-bold opacity-100 mt-1.5 whitespace-nowrap">
                          <Clock size={10} className="opacity-80" /> {res.time} - {getEndTime(res.time, (dragging?.resId === res.id && dragging?.type === 'RESIZE') ? dragging.currentDuration : res.durationMinutes)}
                        </div>
                        <div className="absolute bottom-0 left-0 right-0 h-4 cursor-ns-resize flex items-center justify-center opacity-0 group-hover/block:opacity-100 transition-opacity bg-black/5" onMouseDown={(e) => handleResizeMouseDown(e, res)}>
                          <GripHorizontal size={12} className="text-white/40" />
                        </div>
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderWeekView = () => {
    const weekDays = getWeekDays(currentDate);
    const todayStr = formatDate(new Date());
    const headerHeight = 'h-16';
    return (
      <div className="flex-1 flex flex-col overflow-hidden bg-white rounded-xl border border-slate-200 shadow-sm w-full">
        <div className="overflow-auto relative flex-1" ref={containerRef}>
          <div className="sticky top-0 z-[50] flex w-full min-max">
            <div className={`${headerHeight} bg-slate-100 border-r border-b border-slate-200 shrink-0 sticky left-0 z-[60]`} style={{ width: `${TIME_COL_WIDTH}px` }}></div>
            <div className={`flex flex-1 ${headerHeight} bg-white border-b border-slate-200 shadow-sm`}>
              {weekDays.map((day, idx) => {
                const dStr = formatDate(day);
                const isCollapsed = collapsedDays.has(dStr);
                const dayWidth = isCollapsed ? COLLAPSED_DAY_WIDTH : (sortedTables.length * COL_WIDTH_WEEK);
                const isToday = dStr === todayStr;
                return (
                  <div key={dStr} className={`flex flex-col border-r border-slate-200 shrink-0 transition-all duration-300 ${isToday ? 'bg-emerald-50/30' : (idx % 2 === 0 ? 'bg-white' : 'bg-slate-50')}`} style={{ width: `${dayWidth}px` }}>
                    <div className="px-2 py-1 border-b border-slate-100 text-center flex items-center justify-between relative h-8 overflow-hidden">
                      {isCollapsed ? (
                        <div className="w-full flex flex-col items-center justify-center">
                          <span className="text-[8px] font-black uppercase text-slate-500">{day.toLocaleDateString('de-DE', { weekday: 'short' })}</span>
                          <span className={`text-[10px] font-black ${isToday ? 'text-emerald-600' : 'text-slate-800'}`}>{day.getDate()}</span>
                        </div>
                      ) : (
                        <span className={`text-[10px] font-black uppercase tracking-widest flex-1 text-center ${isToday ? 'text-emerald-600' : 'text-slate-500'}`}>{day.toLocaleDateString('de-DE', { weekday: 'short' })} {day.getDate()}.{day.getMonth() + 1}.</span>
                      )}
                      <button onClick={() => toggleDayCollapse(dStr)} className="absolute right-1 top-1 p-0.5 rounded-md hover:bg-slate-100 text-slate-400">{isCollapsed ? <Plus size={12} /> : <Minus size={12} />}</button>
                    </div>
                    {!isCollapsed && (
                      <div className="flex flex-1 items-center h-8">
                        {sortedTables.map(t => (
                          <div key={`${dStr}-${t.id}`} style={{ flex: `1 0 ${COL_WIDTH_WEEK}px` }} className="shrink-0 px-2 border-r border-slate-50 last:border-r-0 text-center flex items-center justify-center gap-1 h-full">
                            <span className="text-slate-300">{getCategoryIcon(t.category)}</span>
                            <span className={`text-[9px] font-black uppercase tracking-tight truncate block ${isDarkMode ? 'theme-text-primary' : 'text-slate-400'}`}>{t.name}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
          <div className="relative flex w-full min-w-max">
            <div className="sticky left-0 z-[40] bg-slate-50 border-r border-slate-200 flex-shrink-0" style={{ width: `${TIME_COL_WIDTH}px` }}>
              {hours.map(h => (
                <div key={h} style={{ height: `${ROW_HEIGHT}px` }} className="border-b border-slate-200 px-2 pt-1 text-[10px] font-black text-slate-400 text-right bg-slate-50 relative">{h}:00</div>
              ))}
            </div>
            <div className="flex flex-1">
              {weekDays.map((day, idx) => {
                const dateStr = formatDate(day);
                const isCollapsed = collapsedDays.has(dateStr);
                const dayWidth = isCollapsed ? COLLAPSED_DAY_WIDTH : (sortedTables.length * COL_WIDTH_WEEK);
                return (
                  <div key={`col-day-${dateStr}`} className={`flex border-r border-slate-200 shrink-0 transition-all duration-300 ${dateStr === todayStr ? 'bg-emerald-50/10' : (idx % 2 === 0 ? 'bg-white' : 'bg-slate-50')}`} style={{ width: `${dayWidth}px` }}>
                    {isCollapsed ? (
                      <div className="w-full relative flex flex-col group overflow-hidden">
                        {hours.map(h => (
                          <div key={h} style={{ height: `${ROW_HEIGHT}px` }} className="border-b border-slate-100/50 flex items-center justify-center">
                            {reservations.some(r => r.date === dateStr && r.time.startsWith(h.toString().padStart(2, '0')) && r.status !== 'CANCELLED_BY_GUEST') && <div className="w-2 h-2 rounded-full bg-emerald-500"></div>}
                          </div>
                        ))}
                      </div>
                    ) : (
                      sortedTables.map(t => {
                        // Filter out CANCELLED_BY_GUEST so slots are visually free
                        const columnReservations = reservations.filter(r => r.tableId === t.id && r.date === dateStr && r.status !== 'CANCELLED_BY_GUEST');
                        return (
                          <div key={`col-day-${dateStr}-table-${t.id}`} style={{ flex: `1 0 ${COL_WIDTH_WEEK}px` }} className="calendar-col border-r border-slate-50 last:border-r-0 relative group select-none box-border">
                            {hours.map(h => {
                              const isClosed = isSlotClosed(day, h);
                              return (
                                <div key={h} style={{ height: `${ROW_HEIGHT}px`, ...(isClosed ? closedPatternStyle : {}) }} className="border-b border-r border-slate-200 box-border relative">
                                  {!isClosed && (
                                    <>
                                      <div onClick={() => handleSlotClick(t.id, h, dateStr)} className="absolute top-0 left-0 right-0 h-1/2 hover:bg-emerald-50/40 cursor-cell transition-colors"></div>
                                      <div onClick={() => handleSlotClick(t.id, h + 0.5, dateStr)} className="absolute bottom-0 left-0 right-0 h-1/2 hover:bg-emerald-50/40 cursor-cell transition-colors border-t border-slate-50/50"></div>
                                    </>
                                  )}
                                </div>
                              );
                            })}
                            {columnReservations.map(res => (
                              <div key={res.id} style={getReservationBlockStyle(res, columnReservations)} onMouseDown={(e) => handleMouseDown(e, res)} className="absolute border border-white/20 rounded-lg p-2 text-white shadow-sm z-10 hover:brightness-110 cursor-grab active:cursor-grabbing overflow-hidden group/block">
                                <div className="text-xs font-medium truncate leading-tight">{res.customerName}</div>
                                <div className="text-[9px] font-bold mt-1"><Clock size={9} className="inline mr-1" /> {res.time}</div>
                                <div className="absolute bottom-0 left-0 right-0 h-4 cursor-ns-resize flex items-center justify-center opacity-0 group-hover/block:opacity-100 bg-black/5" onMouseDown={(e) => handleResizeMouseDown(e, res)}>
                                  <GripHorizontal size={10} className="text-white/40" />
                                </div>
                              </div>
                            ))}
                          </div>
                        );
                      })
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </div>
    );
  };

  const renderMonthView = () => {
    const days = getMonthDays(currentDate);
    return (
      <div className="flex-1 grid grid-cols-7 border-l border-t border-slate-200 rounded-xl overflow-hidden bg-white shadow-sm w-full">
        {['Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa', 'So'].map(h => <div key={h} className="bg-slate-50 px-3 py-2 border-r border-b border-slate-200 text-center"><span className="text-[10px] font-black text-slate-400 uppercase tracking-widest">{h}</span></div>)}
        {days.map((d, i) => {
          const dateStr = formatDate(d.date);
          // Filter out CANCELLED_BY_GUEST
          const dayRes = reservations.filter(r => r.date === dateStr && r.status !== 'CANCELLED_BY_GUEST');
          const isToday = dateStr === formatDate(new Date());
          return (
            <div 
              key={i} 
              onClick={() => {
                if (!d.isPadding) {
                  setCurrentDate(d.date);
                  setViewType('DAY');
                }
              }}
              className={`min-h-[120px] p-2 border-r border-b border-slate-100 flex flex-col gap-1 transition-colors cursor-pointer hover:bg-slate-50 ${d.isPadding ? 'opacity-40 grayscale cursor-default' : ''} ${isToday ? 'bg-emerald-50/30 hover:bg-emerald-50/50' : ''}`}
            >
              <div className="flex items-center justify-between">
                {!d.isPadding && (
                  <button 
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      setEditingReservation(null); 
                      setModalInitialData({ date: dateStr, time: '12:00' }); 
                      setIsModalOpen(true); 
                    }} 
                    className="p-1 text-slate-300 hover:text-emerald-500 transition-colors"
                  >
                    <Activity size={12} />
                  </button>
                )}
                <span className={`text-xs font-black ml-auto ${isToday ? 'bg-emerald-500 text-white w-6 h-6 rounded-full flex items-center justify-center' : 'text-slate-400'}`}>{d.date.getDate()}</span>
              </div>
              <div className="space-y-1 overflow-y-auto max-h-[80px]">
                {dayRes.slice(0, 3).map(res => (
                  <div 
                    key={res.id} 
                    onClick={(e) => { 
                      e.stopPropagation(); 
                      setEditingReservation(res); 
                      setIsModalOpen(true); 
                    }} 
                    className="px-1.5 py-0.5 text-[8px] font-bold rounded truncate bg-slate-100 text-slate-700 cursor-pointer hover:bg-slate-200 transition-colors"
                  >
                    {res.time} {res.customerName}
                  </div>
                ))}
                {dayRes.length > 3 && <div className="text-[8px] font-black text-slate-400 text-center">+{dayRes.length - 3} weitere</div>}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const dynamicTitle = useMemo(() => {
    if (viewType === 'DAY') return currentDate.toLocaleDateString('de-DE', { weekday: 'long', day: '2-digit', month: 'long', year: 'numeric' });
    if (viewType === 'WEEK') {
      const d = new Date(currentDate); d.setDate(d.getDate() - (d.getDay() === 0 ? 6 : d.getDay() - 1));
      return `KW ${Math.ceil((((d.getTime() - new Date(d.getFullYear(), 0, 1).getTime()) / 86400000) + new Date(d.getFullYear(), 0, 1).getDay() + 1) / 7)}, ${d.getFullYear()}`;
    }
    return currentDate.toLocaleDateString('de-DE', { month: 'long', year: 'numeric' });
  }, [viewType, currentDate]);

  return (
    <div className="h-full w-full flex flex-col gap-6 animate-fade-in relative">
      <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-4 w-full">
        <h2 className="text-xl md:text-2xl font-black text-slate-800 flex items-center gap-2 uppercase italic tracking-tighter">
          <CalendarIcon className="theme-text-primary" /> {dynamicTitle}
        </h2>
        <div className="flex items-center gap-3">
          <div className="flex bg-white border border-slate-200 p-1 rounded-xl shadow-sm">
            <button onClick={() => navigateDate(-1)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-600"><ChevronLeft size={20} /></button>
            <button onClick={() => setCurrentDate(new Date())} className="px-3 text-xs font-black uppercase tracking-widest text-slate-600 hover:text-emerald-600">Heute</button>
            <button onClick={() => navigateDate(1)} className="p-1.5 hover:bg-slate-100 rounded-lg text-slate-600"><ChevronRight size={20} /></button>
          </div>
          <div className="flex bg-slate-100 p-1 rounded-xl border border-slate-200">
            {['DAY', 'WEEK', 'MONTH'].map(v => <button key={v} onClick={() => setViewType(v as ViewType)} className={`px-4 py-2 rounded-lg text-[10px] font-black uppercase tracking-widest transition-all ${viewType === v ? 'theme-bg-secondary text-white shadow-md' : 'text-slate-500 hover:text-slate-800'}`}>{v === 'DAY' ? 'Tag' : v === 'WEEK' ? 'Woche' : 'Monat'}</button>)}
          </div>
        </div>
      </div>
      <div className="flex-1 flex flex-col min-h-0 pb-10 w-full">
        {viewType === 'DAY' && renderDayView()}
        {viewType === 'WEEK' && renderWeekView()}
        {viewType === 'MONTH' && renderMonthView()}
      </div>
      <ReservationModal 
        isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} 
        onSave={(res) => { if (editingReservation) onEditReservation(res); else onMakeReservation(res); }} 
        editingReservation={editingReservation} initialData={modalInitialData}
        tables={tables} categories={categories} reservations={reservations} config={config}
      />
    </div>
  );
};
